import type { BatchProgressView } from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, hasMessage, t } from '@ctcells/i18n'

import { stripStepText } from '../../shell/batchStripModel.ts'

import {
  MANUAL_LOAD_WAIT_STATES,
  TRAY_TRANSIT_STATES,
  UNLOAD_WAIT_STATES,
  forStates,
} from './moduleStateSets.ts'
import type { MonitoringDeviceKey } from './monitoringModel.ts'
import { temporaryStepArgs, type StepArgs, type StepLocation } from './temporaryStepMock.ts'

/**
 * **장비 관점 — 「지금 이 모듈의 어느 구성요소가 일하는가」.**
 *
 * 그림을 바꾸지 않는다. 그림 «위에» 얹을 강조 대상과 한 줄 설명을 계산할 뿐이다.
 *
 * ## 축이 둘이고, 둘 다 서버가 말한 것이다
 *
 * | 축 | 출처 | 성질 |
 * | --- | --- | --- |
 * | **모듈 상태**(주) | `module_status.payload.state` — 1초 주기 | 끊기지 않는다. 상당수가 **구성요소 단위**(`UV`·`PIPETTING`·`TIP_CHANGE`·`ROTATE_DRAIN`) |
 * | **스텝 동작**(보조) | `batch_progress` 의 `result.sequence` · `message` | 사건마다 온다. 상태보다 잘게 말한다 |
 *
 * 🔴 **둘이 어긋나도 하나로 접지 않는다.** 상태가 `MOVING` 인데 동작이 `pick` 이면 **둘 다 사실**이다
 *    (저수준 이동과 원자 동작은 층이 다르다 — `06:111`). 강조 대상을 합집합으로 두고 둘 다 보인다.
 *
 * ## 동작 → 구성요소 대응의 근거 — **짐작이 아니다**
 *
 * ⚠ `specs/vocab/equipment.yaml` 의 동작 29값은 **한글**(`3축 이동`·`UV 노출`)이고 스트림은
 *   **영문**(`move_to`·`expose_uv`)이다. **그 사전으로는 못 잇는다** — 둘 사이 대응표가 없다.
 *   영문 시퀀스명으로 적힌 자리를 근거로 삼는다:
 *
 * | 근거 | 무엇을 말하나 |
 * | --- | --- |
 * | `~/workspace/documents/06_상태·시퀀스.md:111` | `open_door`·`close_door`→후문 · `slide_tray`→트레이 슬라이더 · `pick`·`place`·`rotate`→**모듈 내부 직교로봇** · `move_to`→3모듈 모두 `MOVING` · `gripper_set`→상태축 안 몲 |
 * | 같은 문서 `:113` | `inspect`→**비전**(`MIF-VIS-01`). 관측이라 상태를 바꾸지 않는다 |
 * | `module-core/app/generated/transitions.py` | trigger 열이 영문 시퀀스명이다. `run_centrifugation`(`MIF-CENT-03`)→`RUNNING` · `expose_uv`→`UV` · `invert_drain`→`ROTATE_DRAIN` · `shake`(`MIF-LH-05`)→`SHAKING`. **생성물이고 입력이 `06 §4.2b` 다** |
 * | `module-core/profiles/*.yaml` `supported_sequences` | 모듈별 동작 **전수**(cent 12 · asm 12 · staining 14) |
 *
 * 🔴 **표에 없는 동작이 오면 아무 구성요소도 강조하지 않는다.** 동작 이름만 글자로 보인다 —
 *    「알 수 없는 동작」 같은 말을 지어내지 않는다. 실물 전수에서 관측된 동작은 전부
 *    `supported_sequences` 안이었다(2026-09-21) — 사전 밖 값은 오지 않는다.
 *
 * ## 🔴 「어디로」는 서버가 말하지 않는다
 *
 * 진행 프레임의 payload 키는 **정확히 8개**이고(실물 20,855 프레임 전수 2026-09-21)
 * `target`·`source`·`payload_kind`·`position`·`well` 은 **한 건도 없다.**
 * 모듈은 그 인자를 **갖고 있는데**(`profiles/*.yaml` 이 `pick.required=[payload_kind, source]` 를
 * 요구한다) `publisher.py:360-368` 이 발행에서 뺀다 — BR-45 가 그것을 요청한다.
 *
 * ⇒ 그때까지 `temporaryStepMock.ts` 가 **서버가 실어 보낼 바로 그 모양으로** 채운다.
 *   BR 이 받아들여지면 **목 파일을 걷고 프레임에서 읽는 한 줄**로 끝나고 이 파일은 안 바뀐다.
 */

/** 그림이 강조할 구성요소 키. 🔴 **그림 탭 키와 같은 값이다**(`figureTabs`) — 새 이름을 만들지 않는다 */
export type ActivityComponentKey = string

/**
 * **3축 레일 노드의 키** — 「전체」 보기 맨 윗줄에 그려지는 3축 캐리어(`FocusFigure` 의 `.overview__rail`).
 *
 * 🔴 **여기가 단일 출처다. 그림 쪽에 같은 글자를 또 적지 마라** — 실제로 갈라져서 사고가 났다:
 *    레일 노드는 `'gantry'` 로 적혀 있는데 `*_COMPONENT.stain` 이 내는 키는 전부 `'pip'` 이라
 *    **염색기 레일이 한 번도 안 켜졌다**(2026-09-21 실물 359스텝 관측). 화면은 「피펫팅」이라
 *    적는데 정작 피펫이 어두웠다.
 *
 * ⚠ **탭 키가 아니라 그림 노드 키다.** 「3축 로봇」 탭이 있는 장비는 원심 하나뿐이고
 *   (`figureTabs` 의 `GANTRY_TAB`) 어셈블러·염색기는 레일이 그려지되 누를 수 없다.
 *   강조는 탭 유무와 무관하다 — **부품이 일하면 켜진다.**
 * ⚠ 염색기에서 이 노드의 «이름»은 「피펫 3축」이다(§16-1 — ZEUS LT 피펫 캐리어가 곧 3축이다).
 *   키는 셋이 같고 이름표만 갈린다.
 */
export const RAIL_COMPONENT: ActivityComponentKey = 'gantry'

export interface StepActivity {
  /**
   * 지금 일하는 구성요소 키들. **비어 있으면 아무것도 강조하지 않는다** —
   * 그때 그림은 종전과 한 픽셀도 다르지 않다.
   */
  readonly components: readonly ActivityComponentKey[]
  /** 동작 원값(`move_to`·`expose_uv` …). 대응표에 없어도 **글자로는 보인다** */
  readonly action?: string | undefined
  /** 모듈 내 스텝. ⚠ `0` 은 「없다」가 아니라 첫 스텝이다 */
  readonly step?: number | undefined
  readonly total?: number | undefined
  /**
   * 모듈 상태 원값(`PIPETTING` …). 한글 표기는 표시 계층이 만든다.
   * 🔴 계약 enum 을 그대로 쓴다 — `string` 으로 넓히면 표시 계층이 모르는 값을 받게 된다.
   */
  readonly state?: ModuleSnapshot['state'] | undefined
  /** 「무엇이 어디로」. 서버가 주면 서버 것, 아니면 임시 목(`temporaryStepMock.ts`) */
  readonly args?: StepArgs | undefined
  /**
   * 움직임을 그려도 되는가. 🔴 **일시정지·종착이면 `false`** 다 —
   * 멈춘 장비 위에서 무언가 계속 움직이면 화면이 거짓말을 한다.
   */
  readonly moving: boolean
}

/** 아무 일도 안 일어나는 상태. 🔴 **이것이 기본값이다** */
export const NO_ACTIVITY: StepActivity = { components: [], moving: false }

/**
 * 종착·중단한 실행. 🔴 **여기 걸리면 강조를 지운다** — 끝난 일을 「지금」으로 그리지 않는다.
 * `paused` 는 여기 없다(멈춘 것이지 끝난 것이 아니다 — 아래에서 따로 다룬다).
 */
const TERMINAL_EVENTS: ReadonlySet<string> = new Set(['complete', 'error', 'cancelled', 'aborted'])

/**
 * 동작 → **그 동작을 «하는» 구성요소**. 모듈마다 다르다 — 같은 `move_to` 라도 원심은 3축 그리퍼가,
 * 염색기는 피펫 캐리어가 움직인다(§16-1 — 염색기 3축은 그리퍼가 아니라 피펫 캐리어다).
 *
 * 🔴 **여기 적는 것은 「하는 쪽」이지 「대상」이 아니다.** 둘은 **둘 다 사실**이라 하나로 접지 않는다 —
 *    대상 자리는 `LOCATION_COMPONENT` 가 따로 켠다. 그래서 `aspirate` 는 **피펫 + 시약 베이**가
 *    같이 켜지고, `pick` 은 **3축 + 입구 트레이**가 같이 켜진다.
 *    ⚠ 이 규칙을 어겨 어셈블러의 `pick`·`place`·`move_to` 를 본체(`mod`)에 붙였었다 —
 *      3축이 집는 동안 **본체가 켜지고 레일이 어두웠다.** 되돌리지 마라.
 *
 * ⚠ 값은 탭 키일 필요가 **없다**(`RAIL_COMPONENT` 머리말). 어셈블러·염색기에는 「3축 로봇」 탭이
 *   없지만 레일 노드는 그려지고, 강조는 노드에 걸린다.
 */
const ACTION_COMPONENT: Record<
  Exclude<MonitoringDeviceKey, 'robot'>,
  Readonly<Record<string, ActivityComponentKey>>
> = {
  // 원심분리기 — 3축 그리퍼 탭이 따로 있다(장비 관점에서만 켜진다)
  cent: {
    move_to: 'gantry',
    move_z: 'gantry',
    gripper_set: 'gantry',
    pick: 'gantry',
    place: 'gantry',
    rotate: 'cent', // 로터 회전(`MIF-CENT` · 로터 = 원심 고유 장비)
    run_centrifugation: 'cent',
    wait_for_manual_load: 'trays', // `MIF-CENT-01` — 사람이 입구 트레이에 적재한다
    slide_tray: 'trays',
    inspect: 'vision',
  },
  // 어셈블러 — 3축 그리퍼가 집고 놓는다. 본체(`mod`)는 UV·반전배출처럼 «장비가» 하는 일만
  asm: {
    move_to: RAIL_COMPONENT,
    move_z: RAIL_COMPONENT,
    gripper_set: RAIL_COMPONENT,
    pick: RAIL_COMPONENT,
    place: RAIL_COMPONENT,
    rotate: RAIL_COMPONENT, // 그리퍼 회전축 — 반전/정립(`vocab` 「그리퍼(직교3축) — 회전축」)
    expose_uv: 'mod', // → `UV`
    invert_drain: 'mod', // → `ROTATE_DRAIN`
    slide_tray: 'trays',
    inspect: 'vision',
  },
  /*
   * 염색기 — 🔴 **3축은 피펫 캐리어 자신이다**(§16-1 · ZEUS LT). 액체 동작은 전부 그 캐리어가 한다.
   * ⚠ `pip` 탭은 **시약 베이·팁 랙·폐기통 패널**이지 캐리어가 아니다. 액체 동작을 `pip` 에 붙이면
   *   「피펫팅 중인데 피펫(레일)이 어둡다」가 된다 — 실제로 그랬다. 베이는 **대상**일 때만 켜진다
   *   (`LOCATION_COMPONENT.stain` 의 `reagent_bay`·`tip_bay`·`waste_container`).
   */
  stain: {
    move_to: RAIL_COMPONENT,
    move_z: RAIL_COMPONENT,
    mount_tip: RAIL_COMPONENT,
    discard_tip: RAIL_COMPONENT,
    aspirate: RAIL_COMPONENT,
    dispense_reagent: RAIL_COMPONENT,
    mix: RAIL_COMPONENT,
    // 🔴 이것만 캐리어가 아니다 — **시약 베이의 보관 온도**를 맞추는 일이라 베이 패널이 맞다
    set_reagent_storage_temp: 'pip',
    shake: 'shaker', // `MIF-LH-05` → `SHAKING`
    wait_with_temp_monitoring: 'shaker', // → `INCUBATING`
    slide_tray: 'tray',
    inspect: 'vision',
  },
}

/**
 * 모듈 상태 → 구성요소. **주축이다** — 서버가 1초마다 직접 말하는 값이라 스텝 사이를 메운다.
 *
 * 🔴 **여기 없는 상태는 아무것도 강조하지 않는다.** `IDLE`·`WAITING`·`COMPLETE`·`ERROR` 가
 *    그렇다 — 「작업 중」이어도(`WAITING` 은 스텝 간 대기 · `06 §4.2a`) 어느 부품인지는 말하지 않는다.
 * 근거: `06 §4.2b` 전이표 · `module-core/app/generated/transitions.py`.
 */
const STATE_COMPONENT: Record<
  Exclude<MonitoringDeviceKey, 'robot'>,
  Readonly<Record<string, ActivityComponentKey>>
> = {
  /*
   * 🔴 트레이·수거 국면은 **이름을 나열하지 않고 `moduleStateSets.ts` 의 집합으로 짓는다** — 옛 이름
   *    (`WAITING_FOR_UNLOAD` · `WAITING_FOR_MANUAL_LOAD` …)과 새 이름(`WAITING_FOR_TRAY_UNLOAD` ·
   *    `WAITING_FOR_MANUAL_TRAY_*` · `TRAY_LOADING` · `TRAY_UNLOADING`)이 같이 든다(`06 §4.1` 은퇴 대응).
   *    그 집합 밖의 상태만 여기 이름으로 적는다.
   */
  cent: {
    ...forStates(MANUAL_LOAD_WAIT_STATES, 'trays'),
    ...forStates(UNLOAD_WAIT_STATES, 'trays'),
    ...forStates(TRAY_TRANSIT_STATES, 'trays'),
    LOADING: 'trays',
    UNLOADING: 'trays',
    RUNNING: 'cent',
    MOVING: 'gantry',
  },
  asm: {
    TC_LOAD: 'tc',
    TC_REMOVE: 'tc',
    UV: 'mod',
    ROTATE_DRAIN: 'mod',
    /*
     * 🔴 **`LOADING`·`UNLOADING` 이 어셈블러에 생겼다**(2026-09-26 · 정본 `06` `SM-MOD-02` 재정리 —
     *    「어셈블러 장착 기준으로 LOADING, UNLOADING」). `LOADING`=챔버 장착(입구 트레이 → 스테이지) ·
     *    `UNLOADING`=챔버 회수(스테이지 → 출구 트레이). 원심의 같은 이름과 같이 트레이 쪽을 켠다.
     *    ⚠ 2026-09-22 에 「어셈블러에 없는 상태」라 걷었던 칸이다 — 그때는 참이었고 지금은 아니다.
     * `OUTPUT` 은 은퇴(대응 없음)지만 옛 기록을 그리려 남긴다.
     */
    LOADING: 'trays',
    UNLOADING: 'trays',
    OUTPUT: 'trays',
    ...forStates(UNLOAD_WAIT_STATES, 'trays'),
    ...forStates(TRAY_TRANSIT_STATES, 'trays'),
    // 🔴 저수준 이동은 **3축이** 한다. 본체가 아니다 — `06:111` 이 `move_to`→`MOVING` 을 3축에 건다
    MOVING: RAIL_COMPONENT,
  },
  /*
   * 🔴 염색기의 「피펫이 일하는」 상태는 전부 **캐리어(레일)** 다. 베이 패널(`pip`)이 아니다 —
   *    그래야 「피펫팅」 배지와 그림이 같은 말을 한다. 베이는 대상일 때 `LOCATION_COMPONENT` 가 켠다.
   */
  stain: {
    PIPETTING: RAIL_COMPONENT,
    DISPENSING: RAIL_COMPONENT,
    TIP_CHANGE: RAIL_COMPONENT,
    SHAKING: 'shaker',
    INCUBATING: 'shaker',
    // ⚠ 염색기 `LOADING` 은 은퇴다(`06 §4.1` · 대응 없음). 옛 기록을 그리려 남긴다. `UNLOADING` 은 원래 없다
    LOADING: 'tray',
    ...forStates(UNLOAD_WAIT_STATES, 'tray'),
    ...forStates(TRAY_TRANSIT_STATES, 'tray'),
    MOVING: RAIL_COMPONENT,
  },
}

/**
 * 자리 종류(`location_kind`) → 구성요소. **「무엇이 어디로」의 도착지를 강조하는 데 쓴다.**
 *
 * 어휘 출처는 `module-core/app/generated/sequences/*.schema.json` 의 `location_kind` 설명이다
 * (`inlet_tray`=입구 트레이 거치대 · `outlet_tray`=출구 · `rotor_slot` ·
 *  `counterweight_station`=평형추(R″) 상주 칸 · `front_zone` · `waste_container` 등).
 * ⚠ **정적 enum 이 아니다**(`DEC-12` — 권위는 모듈 런타임 선언) — 그래서 모르는 값은 그냥 넘긴다.
 */
const LOCATION_COMPONENT: Record<
  Exclude<MonitoringDeviceKey, 'robot'>,
  Readonly<Record<string, ActivityComponentKey>>
> = {
  cent: {
    inlet_tray: 'trays',
    outlet_tray: 'trays',
    front_zone: 'trays',
    counterweight_station: 'trays', // R″ 는 C 트레이 전용 칸이다(`06 §4.2b` · 확정 2026-09-01)
    rotor_slot: 'cent',
  },
  asm: {
    inlet_tray: 'trays',
    outlet_tray: 'trays',
    front_zone: 'trays',
    equipment_stage: 'mod',
    tc_rack: 'tc',
    waste_container: 'mod',
  },
  stain: {
    inlet_tray: 'tray',
    outlet_tray: 'tray',
    front_zone: 'tray',
    shaker: 'shaker',
    reagent_bay: 'pip',
    tip_bay: 'pip',
    waste_container: 'pip',
  },
}

/**
 * 지금 이 모듈에서 일어나는 일.
 *
 * 🔴 **네 가지 중 하나라도 걸리면 `NO_ACTIVITY` 다** — 그때 그림은 종전 그대로다:
 *    ⓐ 모듈을 모른다 ⓑ **스냅샷이 stale** ⓒ 진행을 못 들었다(도는 배치 없음) ⓓ 실행이 종착했다
 *
 * ⓑ 가 규약이다 — `L1EventStream.stale` 이 「true 면 UI 는 마지막 값을 현재로 보이지 않는다」이고,
 * 진행 보관소도 구독이 끊기면 통째로 비워진다(`resetStreamProgress`). 둘이 같은 방향이다.
 */
export function stepActivity(
  deviceKey: MonitoringDeviceKey,
  snapshot: ModuleSnapshot | undefined,
  progress: BatchProgressView | undefined,
): StepActivity {
  if (deviceKey === 'robot') return NO_ACTIVITY // 6축은 모듈 그림이 따로 없다
  if (snapshot === undefined || snapshot.stale === true) return NO_ACTIVITY
  if (progress === undefined) return NO_ACTIVITY
  if (progress.eventType !== undefined && TERMINAL_EVENTS.has(progress.eventType)) {
    return NO_ACTIVITY
  }

  const state = snapshot.state ?? undefined
  const action = progress.action
  const args = temporaryStepArgs(deviceKey, action, progress.step, state)

  /*
   * 🔴 **합집합이다.** 상태(주)·동작(보조)·자리(도착지)가 서로 다른 부품을 가리켜도 다 보인다.
   *    ⚠ 차례를 지킨다 — 먼저 넣은 것이 앞에 온다(상태 → 동작 → 자리). 화면이 「대표 하나」를
   *      골라야 할 때 그 첫 칸을 쓴다.
   */
  const components: ActivityComponentKey[] = []
  const add = (key: ActivityComponentKey | undefined) => {
    if (key !== undefined && !components.includes(key)) components.push(key)
  }

  add(state === undefined ? undefined : STATE_COMPONENT[deviceKey][state])
  add(action === undefined ? undefined : ACTION_COMPONENT[deviceKey][action])
  add(locationComponent(deviceKey, args?.source))
  add(locationComponent(deviceKey, args?.target))

  return {
    components,
    action,
    ...(progress.step === undefined ? {} : { step: progress.step }),
    ...(progress.total === undefined ? {} : { total: progress.total }),
    ...(state === undefined ? {} : { state }),
    ...(args === undefined ? {} : { args }),
    // 🔴 일시정지면 강조는 남기되 **움직이지 않는다**. 멈춘 장비를 움직이게 그리지 않는다
    moving: progress.eventType !== 'paused',
  }
}

function locationComponent(
  deviceKey: Exclude<MonitoringDeviceKey, 'robot'>,
  at: StepLocation | undefined,
): ActivityComponentKey | undefined {
  return at === undefined ? undefined : LOCATION_COMPONENT[deviceKey][at.location_kind]
}

/** 그 구성요소가 지금 일하는가. 그림이 강조를 켤 때 쓴다 */
export function isActive(activity: StepActivity, key: ActivityComponentKey): boolean {
  return activity.components.includes(key)
}

/**
 * 「무엇이 어디로」 한 토막(`입구 트레이 3번 → 로터 3번`).
 *
 * 🔴 **모르는 자리 종류는 «지어내지 않고» 그냥 뺀다.** `location_kind` 는 정적 enum 이 아니라
 *    (`DEC-12`) 모듈이 런타임에 새 값을 말할 수 있다 — 사전에 없으면 그 토막이 없는 것이 맞다.
 *    「알 수 없는 자리」를 적지 않는다.
 * ⚠ 지금 이 말의 재료는 **임시 목**이다(`temporaryStepMock.ts`). 서버가 인자를 실어 보내면
 *   같은 키를 그대로 읽으므로 이 함수는 **안 바뀐다**.
 */
export function describeArgs(args: StepArgs | undefined): string | undefined {
  if (args === undefined) return undefined
  const from = describeLocation(args.source)
  const to = describeLocation(args.target)
  if (from !== undefined && to !== undefined) return t('activity.move', { from, to })
  if (to !== undefined) return t('activity.to', { to })
  if (from !== undefined) return from
  const wells = args.target_wells
  return wells === undefined || wells.length === 0
    ? undefined
    : t('activity.wells', { wells: wells.join(', ') })
}

function describeLocation(at: StepLocation | undefined): string | undefined {
  if (at === undefined) return undefined
  const key = `activity.at.${at.location_kind}`
  if (!hasMessage(key)) return undefined
  const name = t(key)
  return at.slot === undefined ? name : t('activity.slot', { name, index: at.slot })
}

/**
 * 그림 옆 **한 줄** — 「작업 중 · 이동 중 · 모듈 내 스텝 15/96 · move_to 시작 · 로터 4번」.
 *
 * 🔴 **여기가 그 글자의 단일 출처다**(신설 2026-09-22). 종전에는 `FocusFigure` 안에서 조립했는데,
 *    장비 탭이 블록을 여럿 그리면서 **같은 말이 그림마다 찍혔고** 그 줄의 길이가 블록 폭을 밀어
 *    스텝마다 그림이 좌우로 흔들렸다(eric — 「텍스트가 추가되고 그러면서 계속 뒤틀려」).
 *    ⇒ 줄을 그림 «밖»으로 한 번만 세우려면 글자를 만드는 곳이 그림 밖에도 있어야 한다.
 * 🔴 **앞 두 토막은 작업 줄 3행과 «글자까지 같다»** — 같은 `stripStepText()` 를 부른다.
 *    두 곳이 다른 말을 하면 안 된다는 규약을 함수 하나로 지킨다.
 * 🔴 모르는 것은 **뺀다.** 「알 수 없음」을 지어 적지 않는다.
 */
export function activityText(
  activity: StepActivity,
  progress: BatchProgressView | undefined,
): string | undefined {
  if (activity.components.length === 0) return undefined
  const parts = [
    t(activity.moving ? 'activity.now' : 'activity.paused'),
    /*
     * 🔴 **`as ModuleState` 캐스트를 쓰지 않는다.** `moduleStateLabel()` 의 서명은 유니온이지만
     *    실제 인자는 서버 문자열이라 캐스트 한 줄이면 타입이 뚫린다 — 실제로 뚫려서
     *    `WAITING_FOR_UNLOAD` 배지가 빈 적이 있다(2026-09-20). `enumLabel()` 은 같은 사전을
     *    보면서 **모르는 값이면 원값을 돌려준다** — 빈 칸이 되지 않는다.
     */
    activity.state === undefined ? undefined : enumLabel('enum.moduleState', activity.state),
    stripStepText(progress),
    describeArgs(activity.args),
  ].filter((part) => part !== undefined && part !== '')
  return parts.length === 0 ? undefined : parts.join(' · ')
}
